import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Typography, Table, Tag, Select, DatePicker, Space, Input, message, Button, Tabs, Card, Statistic,
} from 'antd';
import { CheckOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import UpcomingAbsencePivot from '../components/UpcomingAbsencePivot';
import AbsenceAnalytics from '../components/AbsenceAnalytics';
import SchedulePage from './SchedulePage';
import AbsenceCreateModal from '../components/AbsenceCreateModal';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Submitted',    value: 'Submitted'    },
  { label: 'Acknowledged', value: 'Acknowledged' },
  { label: 'Canceled',    value: 'Canceled'    },
];

const STATUS_COLOR = { Submitted: 'blue', Acknowledged: 'green', Canceled: 'default' };

const REASON_OPTIONS = [
  'Sick/Health Related', 'Jury Duty', 'Bereavement', 'Outage', 'Other',
].map((v) => ({ label: v, value: v }));

const PERIOD_OPTIONS = [
  'Entire Day', 'Absent Part of Day',
].map((v) => ({ label: v, value: v }));

let _cache = null;

// embedded (Campaign Summary > Absences): locked to the summary's team (its
// team pickers hidden), no heading, and its own filters -- the Absences page's
// remembered search (_cache) is neither read nor written.
export default function AbsenceNotificationsPage({ embedded = false, teamId = null }) {
  const cache = embedded ? null : _cache;
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  const isAdmin = currentUser?.Role === 'admin';
  const canEditAbsences = (currentUser?.CanEditAbsences ?? 0) >= 2;

  const restoredRef = useRef(!!cache);
  const [rows, setRows] = useState(() => cache?.rows             ?? []);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => cache?.statusFilter   ?? []);
  const [reasonFilter, setReasonFilter] = useState(() => cache?.reasonFilter   ?? '');
  const [periodFilter, setPeriodFilter] = useState(() => cache?.periodFilter   ?? '');
  const [teamFilter, setTeamFilter]     = useState(() => (embedded ? (teamId ? [teamId] : []) : (_cache?.teamFilter ?? [])));
  const [teamOptions, setTeamOptions]   = useState([]);
  const [userNameFilter, setUserNameFilter] = useState(() => cache?.userNameFilter ?? '');
  const [dateRange, setDateRange] = useState(() => cache?.dateRange ?? [dayjs().subtract(1, 'day'), dayjs().add(30, 'day')]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [acknowledging, setAcknowledging] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);

  // Scorecards: absences covering today and tomorrow, Full vs Partial Day.
  // They follow the Team filter only -- not dates, status or the rest.
  const [dayCounts, setDayCounts] = useState(null);
  const loadDayCounts = useCallback(() => {
    const params = teamFilter.length ? { team_id: teamFilter.join(',') } : {};
    api.get('/api/absences/day-counts', { params })
      .then((r) => setDayCounts(r.data))
      .catch(() => setDayCounts(null));
  }, [teamFilter]);
  useEffect(() => { loadDayCounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    loadDayCounts();
    try {
      const params = {};
      if (statusFilter.length)    params.status         = statusFilter.join(',');
      if (reasonFilter)           params.reason         = reasonFilter;
      if (periodFilter)           params.absence_period = periodFilter;
      if (teamFilter.length)      params.team_id        = teamFilter.join(',');
      if (userNameFilter.trim())  params.user_name      = userNameFilter.trim();
      if (dateRange?.[0])         params.start_date     = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])         params.end_date       = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/api/absences', { params });
      setRows(res.data);
      setSelectedRowKeys([]);
      if (!embedded) _cache = { rows: res.data, statusFilter, reasonFilter, periodFilter, teamFilter, userNameFilter, dateRange };
    } catch {
      message.error('Failed to load absence notifications.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, reasonFilter, periodFilter, teamFilter, userNameFilter, dateRange, loadDayCounts]);

  useEffect(() => { restoredRef.current = false; }, []); // eslint-disable-line

  // The first visit searches with the default filters, so the page does not
  // open empty until Search is clicked; coming back restores the last search.
  useEffect(() => { if (!cache) load(); }, []); // eslint-disable-line

  // Embedded: follow the summary's team, and search again when it changes.
  const firstTeam = useRef(true);
  useEffect(() => {
    if (!embedded) return;
    if (firstTeam.current) { firstTeam.current = false; return; }
    setTeamFilter(teamId ? [teamId] : []);
  }, [embedded, teamId]);
  const teamKey = teamFilter.join(',');
  const firstLoad = useRef(true);
  useEffect(() => {
    if (!embedded) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    load();
  }, [teamKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then((r) => {
      const all = r.data || [];
      const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
      const visible = vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
      setTeamOptions(visible.map((t) => ({ label: t.Name, value: t.ID })));
    }).catch(() => {});
  }, [currentUser]);


  const handleAcknowledge = async () => {
    if (!selectedRowKeys.length) return;
    setAcknowledging(true);
    try {
      const res = await api.post('/api/absences/bulk-acknowledge', { ids: selectedRowKeys });
      message.success(`${res.data.acknowledged} absence${res.data.acknowledged === 1 ? '' : 's'} acknowledged.`);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to acknowledge.');
    } finally {
      setAcknowledging(false);
    }
  };

  // Every column sorts. Times sort by the clock ('hh:mm AM/PM'), not as text.
  const strSort = (k) => (a, b) => String(a[k] ?? '').localeCompare(String(b[k] ?? ''));
  const clockMins = (t) => {
    const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec((t || '').trim());
    if (!m) return -1;
    return ((+m[1] % 12) + (m[3].toUpperCase() === 'PM' ? 12 : 0)) * 60 + +m[2];
  };
  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 65, sorter: (a, b) => a.ID - b.ID },
    { title: 'Employee', dataIndex: 'UserName', key: 'UserName', width: 160, ellipsis: true, sorter: strSort('UserName') },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 130, ellipsis: true, sorter: strSort('TeamName'),
      render: (v) => v || '—' },
    { title: 'Reason', dataIndex: 'Reason', key: 'Reason', ellipsis: true, sorter: strSort('Reason') },
    { title: 'Period', dataIndex: 'AbsencePeriod', key: 'AbsencePeriod', ellipsis: true, sorter: strSort('AbsencePeriod') },
    { title: 'Date Start', dataIndex: 'DateStart', key: 'DateStart', width: 110, sorter: strSort('DateStart') },
    { title: 'Date End', dataIndex: 'DateEnd', key: 'DateEnd', width: 110, sorter: strSort('DateEnd') },
    {
      title: 'Times', key: 'Times', width: 160,
      sorter: (a, b) => clockMins(a.StartTime) - clockMins(b.StartTime),
      render: (_, r) =>
        r.StartTime
          ? `${r.StartTime} – ${r.EndTime || '?'}`
          : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 120, sorter: strSort('Status'),
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Submitted', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175, sorter: strSort('CreatedAt'),
      render: (v) => formatTS(v) },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.Status !== 'Submitted',
    }),
  };

  const tabItems = [
    {
      key: 'list',
      label: 'All Absences',
      children: (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              ['Qty Full Day - Today',       dayCounts?.today?.full,        dayCounts?.today?.full_hours],
              ['Qty Partial Day - Today',    dayCounts?.today?.partial,     dayCounts?.today?.partial_hours],
              ['Qty Full Day - Tomorrow',    dayCounts?.tomorrow?.full,     dayCounts?.tomorrow?.full_hours],
              ['Qty Partial Day - Tomorrow', dayCounts?.tomorrow?.partial,  dayCounts?.tomorrow?.partial_hours],
            ].map(([title, v, hrs]) => (
              <Card key={title} size="small" style={{ minWidth: 190, flex: '1 1 190px', maxWidth: 260 }}>
                <Statistic title={title} value={v ?? '—'} />
                {hrs != null && (
                  <Typography.Text type="secondary">{Number(hrs).toFixed(1)} hrs</Typography.Text>
                )}
              </Card>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Space wrap>
              <Select
                mode="multiple" allowClear placeholder="All statuses"
                options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter}
                style={{ minWidth: 180 }}
              />
              {!embedded && (
                <Select
                  mode="multiple" allowClear showSearch optionFilterProp="label"
                  placeholder="All teams"
                  options={teamOptions}
                  value={teamFilter}
                  onChange={setTeamFilter}
                  maxTagCount="responsive"
                  style={{ minWidth: 180 }}
                />
              )}
              <Select
                allowClear placeholder="Reason"
                options={REASON_OPTIONS}
                value={reasonFilter || undefined}
                onChange={(v) => setReasonFilter(v || '')}
                style={{ minWidth: 200 }}
              />
              <Select
                allowClear placeholder="Period"
                options={PERIOD_OPTIONS}
                value={periodFilter || undefined}
                onChange={(v) => setPeriodFilter(v || '')}
                style={{ minWidth: 220 }}
              />
              <Input.Search
                placeholder="Employee name"
                allowClear
                value={userNameFilter}
                onChange={(e) => setUserNameFilter(e.target.value)}
                onSearch={load}
                style={{ width: 180 }}
              />
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                placeholder={['Date From', 'Date To']}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={load} loading={loading}>Search</Button>
            </Space>
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleAcknowledge}
                loading={acknowledging}
                style={{ flexShrink: 0, marginLeft: 12 }}
              >
                Acknowledge Selected ({selectedRowKeys.length})
              </Button>
            )}
          </div>

          <Table
            rowKey="ID"
            rowSelection={rowSelection}
            dataSource={rows}
            columns={columns}
            loading={loading}
            size="small"
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100'],
              showTotal: (t) => `${t} record${t === 1 ? '' : 's'}`,
            }}
            onRow={(r) => ({ onClick: () => navigate(`/absences/${r.ID}`), style: { cursor: 'pointer' } })}
            locale={{ emptyText: 'No absence notifications found.' }}
          />
        </>
      ),
    },
    {
      key: 'upcoming',
      label: 'Upcoming — 14 Days',
      children: <UpcomingAbsencePivot teamFilter={teamFilter} setTeamFilter={setTeamFilter} teamOptions={teamOptions} lockTeam={embedded} />,
    },
    {
      // The Break & Lunch Schedule for one day, read-only, with absences over
      // it and Scheduled / Out / Break / Available counts per slot.
      key: 'byhour',
      label: 'By Hour',
      children: <SchedulePage embedded initialTeamIds={teamFilter} lockTeams={embedded} />,
    },
    {
      key: 'analytics',
      label: 'Analytics',
      children: <AbsenceAnalytics teamFilter={teamFilter} setTeamFilter={setTeamFilter} teamOptions={teamOptions} lockTeam={embedded} />,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        {embedded ? <span /> : <Typography.Title level={4} style={{ margin: 0 }}>Absence Notifications</Typography.Title>}
        {canEditAbsences && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Submit Absence
          </Button>
        )}
      </div>

      <Tabs defaultActiveKey="list" items={tabItems} />

      <AbsenceCreateModal
        open={createOpen}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { if (!embedded) _cache = null; setCreateOpen(false); load(); }}
      />
    </>
  );
}
